import React from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Web,
  Receipt,
  Email,
  Logout,
  BusinessCenter,
  ContactMail,
  Analytics,
  AccountTree,
  Settings,
  Notifications,
  GroupWork,
  RocketLaunch,
  AutoGraph,
  Bolt,
  Psychology,
  TrendingUp,
  Send,
  Shield,
  MonitorHeart,
  CurrencyBitcoin,
  Air,
  CurrencyExchange,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 260;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

interface MenuSection {
  label: string;
  items: MenuItem[];
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const adminMenuSections: MenuSection[] = [
    {
      label: 'Command',
      items: [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
      ],
    },
    {
      label: 'Revenue',
      items: [
        { text: 'Opportunities', icon: <TrendingUp />, path: '/opportunities' },
        { text: 'Pipeline', icon: <AccountTree />, path: '/pipeline' },
        { text: 'Customers', icon: <GroupWork />, path: '/customers' },
        { text: 'Clients', icon: <People />, path: '/clients' },
        { text: 'Leads', icon: <BusinessCenter />, path: '/leads' },
        { text: 'Contacts', icon: <ContactMail />, path: '/contacts' },
      ],
    },
    {
      label: 'Operations',
      items: [
        { text: 'Sites', icon: <Web />, path: '/sites' },
        { text: 'Invoices', icon: <Receipt />, path: '/invoices' },
        { text: 'Communications', icon: <Send />, path: '/communications' },
        { text: 'Emails', icon: <Email />, path: '/emails' },
      ],
    },
    {
      label: 'Marketing',
      items: [
        { text: 'REV108', icon: <RocketLaunch />, path: '/rev108' },
      ],
    },
    {
      label: 'Intelligence',
      items: [
        { text: 'Stratagem', icon: <Psychology />, path: '/stratagem' },
        { text: 'Attention Engine', icon: <AutoGraph />, path: '/attention-engine' },
        { text: '風の洞察 Intel', icon: <Air />, path: '/kaze' },
        { text: '富の神 Trading', icon: <CurrencyBitcoin />, path: '/tomi' },
        { text: '富の神 Command', icon: <AutoGraph />, path: '/tomi/command' },
        { text: '⚡ Tomi v2 Command', icon: <Bolt />, path: '/tomi/v2' },
        { text: 'TopStep Challenge', icon: <CurrencyExchange />, path: '/topstep' },
        { text: 'TopStep 50K', icon: <CurrencyExchange />, path: '/topstep/50k-challenge' },
        { text: "TopStep - Josh's Challenge", icon: <CurrencyExchange />, path: '/topstep/josh-challenge' },
        { text: 'TopStep Express Funded', icon: <CurrencyExchange />, path: '/topstep/express-funded' },
        { text: 'TopStep Express Funded 2', icon: <CurrencyExchange />, path: '/topstep/express-funded-2' },
        { text: 'TopStep NerdNode Funded', icon: <CurrencyExchange />, path: '/topstep/nerdnode-funded' },
        { text: 'TopStep Account 2', icon: <CurrencyExchange />, path: '/topstep/account2' },
        { text: 'TopStep Account 3', icon: <CurrencyExchange />, path: '/topstep/account3' },
        { text: 'TopStep 100K', icon: <CurrencyExchange />, path: '/topstep/100k-challenge' },
        { text: 'TopStep 150K', icon: <CurrencyExchange />, path: '/topstep/150k-challenge' },
        { text: 'Coinbase Derivatives', icon: <CurrencyBitcoin />, path: '/coinbase' },
      ],
    },
    {
      label: 'Security',
      items: [
        { text: 'Iron Dome', icon: <Shield />, path: '/security' },
        { text: 'Ops Dashboard', icon: <MonitorHeart />, path: '/ops' },
      ],
    },
  ];

  const customerMenuSections: MenuSection[] = [
    {
      label: 'Trading',
      items: [
        { text: 'Bot Dashboard', icon: <CurrencyExchange />, path: '/topstep/bot' },
      ],
    },
    {
      label: 'Account',
      items: [
        { text: 'Account Settings', icon: <Settings />, path: '/account' },
      ],
    },
  ];

  const menuSections = user?.role === 'viewer' ? customerMenuSections : adminMenuSections;

  // Flat list for AppBar title lookup
  const allMenuItems = menuSections.flatMap(s => s.items);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar
        sx={{
          px: 2,
          minHeight: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="h5"
          noWrap
          component="div"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: '#ffffff',
          }}
        >
          REDEX
        </Typography>
      </Toolbar>
      <Divider sx={{ opacity: 0.1 }} />
      
      <Box sx={{ px: 2, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar 
          sx={{ 
            bgcolor: theme.palette.primary.main,
            width: 40,
            height: 40,
            fontSize: '0.9375rem',
            fontWeight: 700,
          }}
        >
          {user?.name?.charAt(0) || 'A'}
        </Avatar>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {user?.name || 'Admin User'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
            {user?.email || 'admin@redex.com'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, flexGrow: 1, overflowY: 'auto' }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.label} sx={{ mb: 1 }}>
            <Typography
              variant="overline"
              sx={{
                px: 1,
                py: 0.5,
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: 'text.secondary',
                opacity: 0.6,
                mt: sectionIndex === 0 ? 0 : 0.5,
              }}
            >
              {section.label}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 1.5,
                      py: 1,
                      minHeight: 40,
                      transition: 'all 0.15s ease-in-out',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                        '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        fontSize: '0.8125rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
      
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={handleLogout}
          startIcon={<Logout />}
          sx={{ 
            borderRadius: 2,
            py: 1,
            borderColor: 'rgba(244, 67, 54, 0.3)',
            '&:hover': {
              borderColor: 'error.main',
              bgcolor: 'rgba(244, 67, 54, 0.05)',
            }
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {allMenuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton color="inherit" size="small">
              <Notifications sx={{ fontSize: '1.25rem' }} />
            </IconButton>
            <IconButton color="inherit" size="small">
              <Settings sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(255, 255, 255, 0.05)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
